import { GetString } from 'fluent-react';
import React, { RefObject } from 'react';
import { CountryProfile, GlobalDatum } from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import { formatTradeValueUSD } from '../../numberFormatters';
import { SlideElementProps } from '../../presentationManager/PresentationManager';
import {
  IndicatorsContainerBase,
} from '../Grid';
import GlossaryTerm, { TooltipAlign, TooltipType } from '../landing/GlossaryTerm';
import Indicator, {
  Status,
  TextWeight,
  Type,
} from '../topPageIndicator/Indicator';

const query = gql`
  query ExportBasketTopOfPageIndicators($id: ID!) {
    location: countryProfile(location: $id) {
      exportValue
      exportValueRank
      currentAccount {
        quantity
        year
      }
    }
    globalData: globalDatum {
      latestExporterRankTotal
    }
  }
`;

interface SuccessResponse {
  location: null | {
    exportValue: CountryProfile['exportValue'] | null
    exportValueRank: CountryProfile['exportValueRank'] | null
    currentAccount: CountryProfile['currentAccount'],
  };
  globalData: {
    latestExporterRankTotal: GlobalDatum['latestExporterRankTotal'],
  };
}

interface Variables {
  id: string;
  year: number;
}

interface Props extends SlideElementProps {
  countryId: string;
  year: number;
  getFluentString: GetString;
  zIndex: number;
}

const TopOfPageIndicators = React.forwardRef((props: Props, rootElRef: RefObject<HTMLDivElement>) => {
  const variables: Variables = {
    year: props.year,
    id: props.countryId,
  };

  const {loading, error, data} = useQuery<SuccessResponse, Variables>(query, {variables});
  const {
    getFluentString, zIndex,
  } = props;
  const totalExportLabel = getFluentString('total-export-indicator-label');
  const exportRankLabel = getFluentString('exporter-rank-indicator-label');

  const tradeBalanceLabel = (
    <div>
      <GlossaryTerm
          type={TooltipType.Standard}
          term={getFluentString('current-account-indicator-label')}
          explanation={getFluentString('glossary-current-account-term-definition')}
          citation={getFluentString('glossary-current-account-term-citation')}
          align={TooltipAlign.Standard}
          attachment={null}
        />
      </div>
  );

  let out: React.ReactElement<any> | null;
  if (loading === true) {
    out = (
      <>
        <Indicator
          type={Type.Text}
          status={Status.Loading}
          label={totalExportLabel}
        />
        <Indicator
          type={Type.Rank}
          status={Status.Loading}
          label={exportRankLabel}
        />
        <Indicator
          type={Type.Text}
          status={Status.Loading}
          label={tradeBalanceLabel}
        />
      </>
    );
  } else if (error !== undefined) {
    console.error(error);
    out = (
      <>
        <Indicator
          type={Type.Text}
          status={Status.Failure}
          label={totalExportLabel}
        />
        <Indicator
          type={Type.Rank}
          status={Status.Failure}
          label={exportRankLabel}
        />
        <Indicator
          type={Type.Text}
          status={Status.Failure}
          label={tradeBalanceLabel}
        />
      </>
    );
  } else if (data !== undefined) {
    const {location, globalData: {latestExporterRankTotal}} = data;
    if (location === null) {
      out = (
        <>
          <Indicator
            type={Type.Text}
            status={Status.Failure}
            label={totalExportLabel}
          />
          <Indicator
            type={Type.Rank}
            status={Status.Failure}
            label={exportRankLabel}
          />
          <Indicator
            type={Type.Text}
            status={Status.Failure}
            label={tradeBalanceLabel}
          />
        </>
      );
    } else {
      const {exportValue, exportValueRank, currentAccount} = location;

      const formattedExportValue = exportValue !== null
            ? formatTradeValueUSD(exportValue, getFluentString) : 'N/A';
      const formattedQuantity = currentAccount !== null
            ? formatTradeValueUSD(currentAccount.quantity, getFluentString) : 'N/A';

      const totalExportsIndicator = (
        <Indicator
          status={Status.Success}
          type={Type.Text}
          label={totalExportLabel}
          text={formattedExportValue}
          weight={TextWeight.Heavy}
        />
      );
      const suffix = (exportValueRank !== null) ?
                      getFluentString('ordinal-rank-without-number', {rank: exportValueRank}) :
                      '';
      const exporterRankIndicator = (
        <Indicator
          type={Type.Rank}
          status={Status.Success}
          label={exportRankLabel}
          numerator={exportValueRank}
          suffix={suffix}
          denominator={getFluentString('exporter-rank-indicator-denominator', {total: latestExporterRankTotal})}
        />
      );
      const tradeBalanceIndicator = (
        <Indicator
          type={Type.Text}
          status={Status.Success}
          label={tradeBalanceLabel}
          text={formattedQuantity}
          weight={TextWeight.Heavy}
        />
      );
      out = (
        <>
          {totalExportsIndicator}
          {exporterRankIndicator}
          {tradeBalanceIndicator}
        </>
      );
    }

  } else {
    out = null;
  }
  const style: React.CSSProperties = {
    zIndex,
  };
  return (
    <IndicatorsContainerBase
      style={style}
      ref={rootElRef}
    >
      {out}
    </IndicatorsContainerBase>
  );

});

export default TopOfPageIndicators;
